<template>
  <core-modal-body>
    <div class="flex flex-col gap-3">
      <div class="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-100/40 via-amber-100/15 to-white p-4 sm:p-4">
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-amber-300">
            <span class="text-xl leading-none">✨</span>
          </div>
          <div class="min-w-0">
            <div class="mb-1 inline-flex items-center rounded-full border border-amber-300 bg-white/90 px-2 py-0.5 text-xs font-medium uppercase tracking-wider text-amber-600">
              AI Enhance
            </div>
            <h2 class="text-2xl sm:text-3xl font-serif leading-tight">Enhance Description</h2>
            <p class="text-sm sm:text-base text-gray-600 mt-1">
              Compare your original text with the AI rewrite, then choose what to keep.
            </p>
          </div>
        </div>
      </div>

      <transition name="f-fade" mode="out-in">
        <div v-if="isError">
          <UAlert
            color="red"
            variant="subtle"
            title="Something went wrong!"
            description="We couldn't enhance your description at this time. Please try again later."
            :ui="{title: 'text-lg', description: 'text-base'}"
          />
          <div class="flex gap-3 mt-4 justify-end">
            <u-button
              size="lg"
              color="charcoal"
              variant="outline"
              :disabled="isLoading"
              icon="i-ri-refresh-line"
              @click="streamRewrite"
            >
              Try Again
            </u-button>
            <u-button size="lg" color="charcoal" variant="outline" @click="cancel" :icon="COMMON_ICONS.close">Close</u-button>
          </div>
        </div>

        <div v-else>
          <div class="grid rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden sm:grid-cols-2">

            <div class="p-4 sm:p-5 flex min-h-[18rem] flex-col gap-4 bg-white">
              <div class="flex items-center justify-between gap-3">
                <label class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm font-medium text-gray-700">
                  <span class="h-2 w-2 rounded-full bg-gray-400" />
                  Original
                </label>
                <span class="text-xs text-gray-500">Editable</span>
              </div>
              <u-textarea
                v-model="original"
                variant="none"
                :padded="false"
                :disabled="isLoading"
                size="xl"
                autoresize
                :maxrows="14"
                class="w-full flex-1"
                :ui="uiTextarea"
              />
            </div>

            <div class="border-t sm:border-l sm:border-t-0 border-gray-200 p-4 sm:p-5 bg-gradient-to-b from-gray-50 to-white flex min-h-[18rem] flex-col gap-4">
              <div class="flex items-center justify-between gap-3">
                <label class="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-800">
                  <span class="h-2 w-2 rounded-full bg-amber-400" />
                  Enhanced
                </label>
                <span v-if="isStreaming" class="text-xs font-medium text-amber-700">Streaming response…</span>
                <span v-else-if="isGenerated" class="text-xs text-gray-600">Generated</span>
              </div>

              <transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 -translate-y-1 max-h-0"
                enter-to-class="opacity-100 translate-y-0 max-h-20"
                leave-active-class="transition-all duration-250 ease-in"
                leave-from-class="opacity-100 translate-y-0 max-h-20"
                leave-to-class="opacity-0 -translate-y-1 max-h-0"
              >
                <div
                  v-if="isLoading"
                  class="overflow-hidden flex items-center justify-between gap-4 rounded-lg border border-amber-100 bg-amber-50/70 px-3 py-2 text-gray-700"
                >
                  <div class="flex items-center gap-2">
                    <core-busy-dots :width="65" />
                    <p class="text-base">Generating…</p>
                  </div>
                  <u-button
                    v-if="isStreaming"
                    size="md"
                    color="white"
                    :icon="COMMON_ICONS.close"
                    @click="stopStreaming"
                  >
                    Cancel
                  </u-button>
                </div>
              </transition>

              <u-textarea
                v-model="enhanced"
                :disabled="isLoading"
                variant="none"
                :padded="false"
                size="xl"
                autoresize
                :maxrows="14"
                class="w-full flex-1"
                :ui="uiTextarea"
              />

            </div>
          </div>
          <u-alert
            v-if="isTruncated"
            color="red"
            variant="subtle"
            title="Response was cut off"
            description="The AI response hit a length limit. Try a shorter description for a complete result."
            class="my-4"
            :ui="{title: 'text-lg', description: 'text-base'}"
          />
          <div class="flex gap-3 mt-3 items-center sm:justify-between flex-col sm:flex-row rounded-xl border border-gray-200 bg-gray-50/70 p-3 sm:p-4">
            <u-button
              label="Provide Feedback" :padded="false"
              target="_blank"
              to="https://forms.gle/LSg9pn4xXEfUm9oM6"
              variant="link"
              color="charcoal"
              icon="i-ri-feedback-line"
              class="order-2 sm:order-none opacity-80 hover:opacity-100"
            />

            <div class="gap-3 hidden sm:flex items-center">
              <u-button size="lg" color="charcoal" variant="outline" @click="cancel" :icon="COMMON_ICONS.close">Keep Original</u-button>
              <u-button :disabled="!canAccept" size="lg" color="charcoal" class="opacity-90" @click="accept({keepBoth: true})" icon="i-ri-check-line">Keep Both</u-button>
              <u-button :disabled="!canAccept" size="lg" class="shadow-sm" @click="accept" icon="i-ri-check-double-line">Accept Enhanced</u-button>
            </div>

            <div class="grid gap-3 mt-4 grid-cols-2 sm:hidden w-full">
              <u-button :disabled="!canAccept" block class="col-span-2" size="lg" @click="accept" icon="i-ri-check-double-line">Accept Enhanced</u-button>
              <u-button size="lg" color="charcoal" variant="outline" block @click="cancel" :icon="COMMON_ICONS.close">Keep Original</u-button>
              <u-button :disabled="!canAccept" size="lg" color="charcoal" block class="opacity-90" @click="accept({keepBoth: true})" icon="i-ri-check-line">Keep Both</u-button>
            </div>
          </div>

        </div>

      </transition>
    </div>

  </core-modal-body>

</template>

<script setup>
  import {CONTEXTS} from '~/server/utils/rewrite-constants.js';
  import {COMMON_ICONS} from '~/constants/icons.js';
  import {useAIApi} from '~/composables/ai/api.js';

  const emit = defineEmits(['accept']);
  const props = defineProps({
    text: String,
    entityContext: {
      type: String,
      default: CONTEXTS.GENERAL
    },
    modal: Object
  });

  const aiApi = useAIApi();
  const original = ref(props.text);
  const enhanced = ref('');
  const isGenerated = ref(false);
  const isLoading = ref(false);
  const isStreaming = ref(false);
  const isError = ref(false);
  const isTruncated = ref(false);
  const cancelRewrite = ref(null);
  const hasEnhancedText = computed(() => !!enhanced.value?.trim());
  const canAccept = computed(() => !isLoading.value && hasEnhancedText.value);

  const uiTextarea = {
    size: {
      xl: 'text-lg'
    },
    base: 'disabled:cursor-default scrollbar-light'
  };

  function cleanupController() {
    if (cancelRewrite.value) {
      cancelRewrite.value();
      cancelRewrite.value = null;
    }
  }

  function cancel() {
    cleanupController();
    isStreaming.value = false;
    isLoading.value = false;
    useGtm().trackEvent({
      event: 'core_ui-ai-enhance_cancel',
      action: 'click',
      category: 'ai'
    });

    props.modal.close();
  }

  function accept({keepBoth} = {}) {
    if (!canAccept.value) {
      return;
    }

    useGtm().trackEvent({
      event: keepBoth ? 'core_ui-ai-enhance_accept_both' : 'core_ui-ai-enhance_accept',
      action: 'click',
      category: 'ai'
    });

    if (keepBoth) {
      emit('accept', `${original.value}\n\n-----\n\n${enhanced.value}`)
    } else {
      emit('accept', enhanced.value)
    }
  }

  function stopStreaming() {
    if (!isStreaming.value) {
      return;
    }

    cleanupController();
    isStreaming.value = false;
    isLoading.value = false;

    useGtm().trackEvent({
      event: 'core_ui-ai-enhance_stop_stream',
      action: 'click',
      category: 'ai'
    });
  }

  async function streamRewrite() {
    if (!original.value?.trim()) {
      isError.value = true;
      return;
    }

    cleanupController();
    isLoading.value = true;
    isStreaming.value = true;
    isError.value = false;
    isTruncated.value = false;
    isGenerated.value = false;
    enhanced.value = '';

    const {cancel, done} = aiApi.rewrite({
      text: original.value,
      context: props.entityContext,
      onDelta: (text) => {
        enhanced.value += text;
      },
      onError: () => {
        isError.value = true;
      },
      onDone: ({reason} = {}) => {
        if (reason === 'max_tokens') {
          isTruncated.value = true;
          useGtm().trackEvent({
            event: 'core_ui-ai-enhance_truncated',
            action: 'truncate',
            category: 'ai'
          });
          enhanced.value = enhanced.value.replace(/\s*\bMore\b\s*$/i, '').trimEnd();
        }

        if (!isError.value && hasEnhancedText.value) {
          isGenerated.value = true;
        }
      },
    });

    cancelRewrite.value = cancel;

    try {
      await done;
    } catch (e) {
      if (e.name !== 'AbortError') {
        isError.value = true;
      }
    } finally {
      isStreaming.value = false;
      isLoading.value = false;
      if (!isError.value && hasEnhancedText.value) {
        isGenerated.value = true;
      }
      cleanupController();
    }
  }

  onMounted(streamRewrite);
  onBeforeUnmount(stopStreaming);
</script>
